import React, { memo, useCallback, useEffect, useMemo, useState } from 'react'
import tw from 'twin.macro'
import styled from 'styled-components'
import { useTranslation } from 'contexts/Localization'
import { useSelector } from 'react-redux'
import {
  SlButton,
  SlDropdown,
  SlIcon,
  SlIconButton,
  SlMenu,
  SlMenuItem,
  SlSwitch,
  SlInput,
} from '@shoelace-style/shoelace/dist/react'
import { TokenList, Version } from '@uniswap/token-lists'
import { UNSUPPORTED_LIST_URLS } from 'config/constants/lists'
import uriToHttp from 'utils/uriToHttp'
import useFetchListCallback from 'hooks/useFetchListCallback'

import { AppState, useAppDispatch } from 'state'
import { acceptListUpdate, disableList, enableList, removeList } from 'state/lists/actions'
import { useActiveListUrls, useAllLists, useIsListActive } from 'state/lists/hooks'
import { ListLogo } from '../Logo'
import { CurrencyModalView } from './types'

const StyledListMenuTrigger = styled(SlIconButton)`
  ${tw`align-middle`}
  ::part(base) {
    ${tw`py-4`}
  }
`

function listVersionLabel(version: Version): string {
  return `v${version.major}.${version.minor}.${version.patch}`
}

const ListRow = memo(({ listUrl }: { listUrl: string }) => {
  const listsByUrl = useSelector<AppState, AppState['lists']['byUrl']>((state) => state.lists.byUrl)
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const isActive = useIsListActive(listUrl)

  const { current: list, pendingUpdate } = listsByUrl[listUrl]

  const handleViewList = useCallback(() => {
    window.open(`https://tokenlists.org/token-list?url=${listUrl}`, '_blank', 'noopener, noreferrer')
  }, [listUrl])

  const handleUpdateList = useCallback(() => {
    if (!pendingUpdate) {
      return
    }

    dispatch(acceptListUpdate(listUrl))
  }, [dispatch, listUrl, pendingUpdate])

  const handleRemoveList = useCallback(() => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Please confirm you would like to remove this list')) {
      dispatch(removeList(listUrl))
    }
  }, [dispatch, listUrl])

  const handleEnableList = useCallback(() => {
    dispatch(enableList(listUrl))
  }, [dispatch, listUrl])

  const handleDisableList = useCallback(() => {
    dispatch(disableList(listUrl))
  }, [dispatch, listUrl])

  const handleListMenuSelect = (event) => {
    switch (event.detail?.item?.value) {
      case 'view':
        handleViewList()
        break
      case 'remove':
        handleRemoveList()
        break
      case 'update':
        handleUpdateList()
        break
      default:
        break
    }
  }

  const handleListSwitch = () => {
    if (isActive) {
      handleDisableList()
    } else {
      handleEnableList()
    }
  }

  if (!list) {
    return null
  }

  return (
    <div className="bg-dark-gray-900 text-white rounded-12 px-12 py-16" key={listUrl}>
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-8">
          <ListLogo size="40px" logoURI={list.logoURI} alt={`${list.name} list logo`} />
          <div className="flex flex-col justify-between">
            <div className="flex items-center">
              <span className="text-18 font-medium">{list.name}</span>
              <SlDropdown>
                <StyledListMenuTrigger name="setting" library="unicons" slot="trigger" />
                <SlMenu onSlSelect={handleListMenuSelect}>
                  <SlMenuItem value="view" disabled={!listUrl}>
                    {t('View list')}
                    <SlIcon name="external-link-alt" library="unicons" slot="prefix" />
                  </SlMenuItem>
                  <SlMenuItem value="remove" disabled={Object.keys(listsByUrl).length === 1}>
                    {t('Remove list')}
                    <SlIcon name="trash-alt" library="unicons" slot="prefix" />
                  </SlMenuItem>
                  <SlMenuItem value="update" disabled={!pendingUpdate}>
                    {t('Update list')}
                    <SlIcon name="cloud-download" library="unicons" slot="prefix" />
                  </SlMenuItem>
                </SlMenu>
              </SlDropdown>
            </div>
            <div className="text-12 lowercase text-dark-gray-100 flex items-center gap-8">
              <span>{listVersionLabel(list.version)}</span>
              <span>
                {list.tokens.length} {t('Tokens')}
              </span>
            </div>
          </div>
        </div>
        <SlSwitch checked={isActive} onSlChange={handleListSwitch} />
      </div>
    </div>
  )
})

function ManageLists({
  setModalView,
  setImportList,
  setListUrl,
}: {
  setModalView: (view: CurrencyModalView) => void
  setImportList: (list: TokenList) => void
  setListUrl: (url: string) => void
}) {
  const [listUrlInput, setListUrlInput] = useState<string>('')

  const { t } = useTranslation()

  const lists = useAllLists()

  // sort by active but only if not visible
  const activeListUrls = useActiveListUrls()
  const [activeCopy, setActiveCopy] = useState<string[] | undefined>()
  useEffect(() => {
    if (!activeCopy && activeListUrls) {
      setActiveCopy(activeListUrls)
    }
  }, [activeCopy, activeListUrls])

  const handleInput = useCallback((e) => {
    setListUrlInput(e.target.value)
  }, [])

  const fetchList = useFetchListCallback()

  const validUrl: boolean = useMemo(() => uriToHttp(listUrlInput).length > 0, [listUrlInput])

  const sortedLists = useMemo(() => {
    const listUrls = Object.keys(lists)
    return listUrls
      .filter(
        (listUrl) =>
          // only show loaded lists, hide unsupported lists
          Boolean(lists[listUrl].current) && !UNSUPPORTED_LIST_URLS.includes(listUrl),
      )
      .sort((u1, u2) => {
        const { current: l1 } = lists[u1]
        const { current: l2 } = lists[u2]

        // first filter on active lists
        if (activeCopy?.includes(u1) && !activeCopy?.includes(u2)) {
          return -1
        }
        if (!activeCopy?.includes(u1) && activeCopy?.includes(u2)) {
          return 1
        }

        if (l1 && l2) {
          return l1.name.toLowerCase() < l2.name.toLowerCase()
            ? -1
            : l1.name.toLowerCase() === l2.name.toLowerCase()
              ? 0
              : 1
        }
        if (l1) return -1
        if (l2) return 1
        return 0
      })
  }, [lists, activeCopy])

  // temporary fetched list for import flow
  const [tempList, setTempList] = useState<TokenList>()
  const [addError, setAddError] = useState<string>('')

  useEffect(() => {
    async function fetchTempList() {
      fetchList(listUrlInput, false)
        .then((list) => setTempList(list))
        .catch(() => setAddError(t('Error importing list')))
    }

    // if valid url, fetch details for card
    if (validUrl) {
      fetchTempList()
    } else {
      setTempList(null)
      if (listUrlInput !== '') {
        setAddError(t('Enter valid list location'))
      }
    }

    if (listUrlInput === '') {
      setAddError('')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchList, listUrlInput, validUrl])

  // check if list is already imported
  const isImported = Object.keys(lists).includes(listUrlInput)

  // set list values and have parent modal switch to import list view
  const handleImport = useCallback(() => {
    if (tempList) {
      setImportList(tempList)
      setModalView(CurrencyModalView.importList)
      setListUrl(listUrlInput)
    }
  }, [listUrlInput, setImportList, setListUrl, setModalView, tempList])

  return (
    <div className="h-full w-full">
      <div className="space-y-8">
        <SlInput
          id="list-add-input"
          size="large"
          placeholder={t('https:// or ipfs://')}
          value={listUrlInput}
          onSlInput={handleInput}
          invalid={listUrlInput !== '' || isImported}
        >
          {addError && <div slot="help-text">{addError}</div>}
          {isImported && (
            <div slot="help-text" className="text-orange">
              {t('Token list is already imported')}
            </div>
          )}
        </SlInput>
      </div>

      {tempList && !isImported && (
        <div className="bg-dark-gray-900 text-white rounded-12 px-12 py-16 mt-16">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-8">
              <ListLogo size="40px" logoURI={tempList.logoURI} alt={`${tempList.name} list logo`} />
              <div className="flex flex-col justify-between">
                <div className="flex items-center">
                  <span className="text-18 font-medium">{tempList.name}</span>
                </div>
                <div className="text-12 lowercase text-dark-gray-100 flex items-center gap-8">
                  <span>{listVersionLabel(tempList.version)}</span>
                  <span>
                    {tempList.tokens.length} {t('Tokens')}
                  </span>
                </div>
              </div>
            </div>
            <SlButton variant="text" onClick={handleImport}>
              <SlIcon name="import" library="unicons" slot="prefix" />
              {t('Import')}
            </SlButton>
          </div>
        </div>
      )}

      <div className="pt-24 h-full overflow-auto">
        <div className="flex flex-col gap-16">
          {sortedLists.map((listUrl) => (
            <ListRow key={listUrl} listUrl={listUrl} />
          ))}
        </div>
      </div>
    </div>
  )
}

export default ManageLists
